import React, { Fragment, useState } from "react";
import ModalNew from "../popup/Modal";
import { PopupHeader, PopupContent, PopupSection } from "../popup/common";
import ApiResponseModal from "../popup/ApiResponseModal";
import SmallAlert from "../alerts/SmallAlert";
import LoadingPlaceholder from "../loading/LoadingPlaceholder";
import getAlertTitle from "../../../utils/getAlertTitle";
import { createSupportRequest } from "../../../api/account/supportAPI";
import { useGetAccountQuery } from "../../../api/account";
import classes from "../popup/Modal.module.css";

const AccountDeletionModal = ({ setIsModalOpen }) => {
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alertType, setAlertType] = useState(null);
  const [message, setMessage] = useState("");

  // ApiResponseModal states
  const [isResultModalOpen, setIsResultModalOpen] = useState(false);
  const [isResultLoading, setIsResultLoading] = useState(false);
  const [isResultSuccess, setIsResultSuccess] = useState(false);
  const [resultMessage, setResultMessage] = useState("");

  const {
    data: accountInfo,
    isLoading: isAccountLoading,
    isError,
  } = useGetAccountQuery();

  const disabled = isSubmitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setAlertType(null);
    setMessage("");

    setIsResultModalOpen(true);
    setIsResultLoading(true);
    setIsResultSuccess(false);

    try {
      const apiResponse = await createSupportRequest({
        email: accountInfo?.email,
        user_name: accountInfo?.user_name,
        comment: comment || "User submitted an account deletion request.",
        request_type: "ACT", // Account Management
      });

      const data = await apiResponse.json();

      if (apiResponse.ok) {
        setIsResultSuccess(true);
        setResultMessage(
          "Your account deletion request has been submitted successfully. Our team will review your request and reach out shortly.",
        );
      } else {
        setIsResultSuccess(false);
        setResultMessage(
          data.message || "Failed to submit request. Please try again.",
        );
      }
    } catch (error) {
      setIsResultSuccess(false);
      setResultMessage(error.message || "An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
      setIsResultLoading(false);
    }
  };

  const handleClose = () => {
    setComment("");
    setIsModalOpen(false);
  };

  const handleCloseResultModal = () => {
    setIsResultModalOpen(false);
    if (isResultSuccess) {
      handleClose();
    }
  };

  if (isAccountLoading) {
    return (
      <ModalNew className="content-modal">
        <PopupHeader
          title="Request Account Deletion"
          setOpenState={setIsModalOpen}
          icon={`${process.env.PUBLIC_URL}/images/icons/key_blue_popup.svg`}
          variant="compact"
        />
        <PopupContent>
          <div className={classes.modalLoadingContainer}>
            <LoadingPlaceholder />
          </div>
        </PopupContent>
      </ModalNew>
    );
  }

  if (isError) {
    return (
      <ModalNew className="content-modal">
        <PopupHeader
          title="Request Account Deletion"
          setOpenState={setIsModalOpen}
          icon={`${process.env.PUBLIC_URL}/images/icons/key_blue_popup.svg`}
          variant="compact"
        />
        <PopupContent>
          <div className={classes.modalErrorContainer}>
            <SmallAlert
              alert={true}
              type="error"
              title="Error"
              message="Could not load account information. Please try again later."
            />
            <button type="button" onClick={handleClose} className="btn btnBox">
              <b>Close</b>
            </button>
          </div>
        </PopupContent>
      </ModalNew>
    );
  }

  return (
    <Fragment>
      {!isResultModalOpen && (
        <ModalNew className="content-modal">
          <PopupHeader
            title="Request Account Deletion"
            setOpenState={setIsModalOpen}
            icon={`${process.env.PUBLIC_URL}/images/icons/support_blue_popup.svg`}
            variant="compact"
          />

          <PopupContent>
            {alertType && (
              <div className="alert-container" style={{ marginBottom: "20px" }}>
                <SmallAlert
                  alert={true}
                  title={getAlertTitle(alertType)}
                  type={alertType}
                  message={message}
                  dismissOnClickOutside={true}
                  dismissOnFocusOutside={false}
                />
              </div>
            )}

            <div className={classes.modalFormSection}>
              <div className="form-field">
                <input
                  type="email"
                  value={accountInfo?.email || ""}
                  disabled
                  required
                  className="form-input"
                  placeholder="Email"
                />
              </div>

              <div className="form-field">
                <textarea
                  rows="4"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  disabled={disabled}
                  maxLength={500}
                  className="form-textarea"
                  placeholder="Reason for leaving or additional comments (optional)..."
                ></textarea>
                <small className="character-count">
                  {comment.length}/500 characters
                </small>
              </div>

              <button
                type="button"
                onClick={handleSubmit}
                className={`cta-button primary ${disabled ? "disabled" : ""}`}
                disabled={disabled}
                style={{ width: "100%", marginTop: "12px" }}
              >
                <b>
                  {isSubmitting
                    ? "Submitting Request..."
                    : "Submit Deletion Request"}
                </b>
              </button>
            </div>
          </PopupContent>
        </ModalNew>
      )}

      <ApiResponseModal
        isOpen={isResultModalOpen}
        isLoading={isResultLoading}
        isSuccess={isResultSuccess}
        message={resultMessage}
        onClose={handleCloseResultModal}
      />
    </Fragment>
  );
};

export default AccountDeletionModal;
